import { api } from "@/lib/api";

// Client-side analytics helper for the LifeGuide cross-product integration.
// - Captures first-touch sponsor attribution from the URL and persists it for the session.
// - Sends events to our own backend (/analytics/forward), which fingerprints the query and
//   signs+forwards to LifeGuide server-side. The shared secret is NEVER in the browser.
// - Every call is fire-and-forget and can never break the consumer experience.

const ATTR_KEY = "inap_attribution";
const VISITOR_KEY = "inap_visitor_id";
const SESSION_KEY = "inap_session_id";

function uuid() {
  try { return crypto.randomUUID(); }
  catch { return "id-" + Math.random().toString(36).slice(2) + Date.now().toString(36); }
}

// Sponsor attribution params we recognize (first-touch wins, persisted in localStorage).
const ATTR_PARAMS = {
  referral_code: ["referral_code", "ref", "rc"],
  sponsor_slug: ["sponsor_slug", "sponsor"],
  sponsor_id: ["sponsor_id"],
  partner_id: ["partner_id", "partner"],
};

export function captureAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    const found = {};
    for (const [key, aliases] of Object.entries(ATTR_PARAMS)) {
      for (const a of aliases) {
        const v = params.get(a);
        if (v) { found[key] = v; break; }
      }
    }
    if (Object.keys(found).length) {
      const existing = getAttribution();
      // First-touch: only fill fields not already stored.
      const merged = { ...found, ...existing };
      localStorage.setItem(ATTR_KEY, JSON.stringify(merged));
    }
  } catch { /* ignore */ }
}

export function getAttribution() {
  try { return JSON.parse(localStorage.getItem(ATTR_KEY) || "{}"); }
  catch { return {}; }
}

export function getVisitorId() {
  try {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) { v = uuid(); localStorage.setItem(VISITOR_KEY, v); }
    return v;
  } catch { return uuid(); }
}

export function getSessionId() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) { s = uuid(); sessionStorage.setItem(SESSION_KEY, s); }
    return s;
  } catch { return uuid(); }
}

// Fire-and-forget event. `extra` may include: topic, path, query (raw, fingerprinted server-side),
// channel, proverb_id.
export function track(event_type, extra = {}) {
  try {
    const payload = {
      event_type,
      path: extra.path || (typeof window !== "undefined" ? window.location.pathname : undefined),
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      attribution: getAttribution(),
      ...extra,
    };
    api.post("/analytics/forward", payload).catch(() => {});
  } catch { /* never throw */ }
}

// Local engagement counter (shows in this app's own admin dashboard). Fire-and-forget.
export function trackLocal(type) {
  try { api.post("/analytics/event", { type }).catch(() => {}); } catch { /* never throw */ }
}
